import { useMutation } from "@tanstack/react-query";
import { useState } from "react";
import { useDeltaToastContext } from "../../../shared/contexts/ToasterContext";
import { useUserContext } from "../../../shared/contexts/UserContext";
import { handleErr } from "../standards/common/util";
import axios from "axios";
import { deltamathAPI } from "../../utils";
import Button from "../../../student/components/generic/button";
import { useDocumentTitle } from "usehooks-ts";

// Standalone function for uploading the file
const uploadSearchTags = async ({
  file,
  token,
}: {
  file: File;
  token: string;
}) => {
  const formData = new FormData();
  formData.append("file", file);
  const response = await axios.request({
    method: "post",
    url: deltamathAPI() + "/manager_new/module/bulk_edit_search_tags",
    headers: {
      "Content-Type": "multipart/form-data",
      Authorization: `Bearer ${token}`,
    },
    data: formData,
  });
  return response.data;
};

/**
 * Utility to allow manager users to bulk edit search tags using a CSV file.
 */
export const BulkEditSearchTags = () => {
  useDocumentTitle("Bulk Edit Search Tags");
  const toastContext = useDeltaToastContext();
  const userContext = useUserContext();
  const token = userContext.getJWT();
  const [selectedCSV, setSelectedCSV] = useState<File | null>(null);

  // Use the useMutation hook
  const { mutate: uploadSearchTagFile } = useMutation({
    mutationFn: (file: File) => uploadSearchTags({ file, token }),
    onSuccess: (data) => {
      // Handle success
      toastContext.addToast({
        status: "Success",
        message: "Upload Complete",
      });
    },
    onError: (error) => {
      // Handle error
      toastContext.addToast({ status: "Error", message: handleErr(error) });
    },
  });

  return (
    <>
      <div className="px-6 pt-10">
        <h2 className="mb-6 text-2xl font-bold">Bulk Edit Search Tags</h2>
        <div className="mb-3 w-2/5">
          <label htmlFor="searchTagUpload" className="mb-2 inline-block">
            Upload a CSV file to <strong>bulk edit search tags</strong> for
            modules. The CSV file should contain the following columns:
            <ul className="list-disc pl-6">
              <li>Skill Code</li>
              <li>Search Tags and Associations (comma separated list)</li>
            </ul>
          </label>
          <input
            className="focus:border-primary focus:shadow-te-primary dark:focus:border-primary relative m-0 block w-full min-w-0 flex-auto rounded border border-solid border-neutral-500 bg-clip-padding px-3 py-[0.32rem] text-base font-normal text-neutral-700 transition duration-300 ease-in-out file:-mx-3 file:-my-[0.32rem] file:overflow-hidden file:rounded-none file:border-0 file:border-solid file:border-inherit file:bg-neutral-100 file:px-3 file:py-[0.32rem] file:text-neutral-700 file:transition file:duration-150 file:ease-in-out file:[border-inline-end-width:1px] file:[margin-inline-end:0.75rem] hover:file:bg-neutral-200 focus:text-neutral-700 focus:outline-none dark:border-neutral-600 dark:text-neutral-200 dark:file:bg-neutral-700 dark:file:text-neutral-100"
            type="file"
            id="searchTagUpload"
            onChange={(event) => {
              if (event.target.files?.[0]) {
                setSelectedCSV(event.target.files[0]);
              } else {
                setSelectedCSV(null); // Reset if no file is selected
              }
            }}
          />
        </div>
        <Button
          onClick={() => {
            if (selectedCSV) {
              uploadSearchTagFile(selectedCSV);
              setSelectedCSV(null); // Reset the input after upload
            }
          }}
          disabled={!selectedCSV}
        >
          Upload
        </Button>
      </div>
    </>
  );
};
